import type { AxiosError, AxiosRequestConfig } from 'axios';
import agentforgeHttp from '@/lib/agentforge-http';
import { ensureFreshKulaToken } from './kulaToken';
import type {
  AgentForgeApiErrorArgs,
  CallOptions,
  HttpMethod,
} from './http.types';

// Self-contained AgentForge API helper. Mirrors the envelope handling shape
// of `src/api/request.ts` (`callApi`) but talks to the AgentForge axios
// client directly. Keeps the bar's data layer self-contained per the
// `agentforge/` boundary.
//
// `agentforgeHttp` already strips the axios envelope (`r => r.data`), so the
// value resolved here is the raw response body. Callers extract domain
// shapes like `{ sessions }` or `{ session }` themselves.

export class AgentForgeApiError extends Error {
  readonly status: number;
  readonly errors: string[];
  readonly code?: string;
  readonly responseData: unknown;

  constructor(args: AgentForgeApiErrorArgs) {
    super(args.message);
    this.name = 'AgentForgeApiError';
    this.status = args.status;
    this.errors = args.errors;
    this.code = args.code;
    this.responseData = args.responseData;
  }
}

const toStringList = (value: unknown): string[] =>
  Array.isArray(value) ? value.map(String) : [];

const extractError = (body: unknown): AgentForgeApiError | null => {
  if (body === null || body === undefined) return null;
  if (typeof body !== 'object') return null;

  const env = body as { status?: unknown; errors?: unknown };

  // authInterceptor resolves 4xx as fulfilled with `{ status, errors }`.
  if (typeof env.status === 'number' && env.status >= 400) {
    const errors = toStringList(env.errors);
    return new AgentForgeApiError({
      status: env.status,
      errors,
      code: errors[0],
      responseData: body,
      message: errors[0] ?? `request_failed_status_${env.status}`,
    });
  }

  // 2xx envelope with a non-empty errors array (partial-success shape).
  if (Array.isArray(env.errors) && env.errors.length > 0) {
    const errors = toStringList(env.errors);
    return new AgentForgeApiError({
      status: 400,
      errors,
      code: errors[0],
      responseData: body,
      message: errors[0] ?? 'request_failed_errors',
    });
  }

  return null;
};

const dispatch = (
  method: HttpMethod,
  url: string,
  body: unknown,
  config: AxiosRequestConfig
): Promise<unknown> => {
  if (method === 'GET') return agentforgeHttp.get(url, config);
  if (method === 'DELETE') return agentforgeHttp.delete(url, config);
  return agentforgeHttp.request({ method, url, data: body, ...config });
};

export async function callAgentForge<T>(opts: CallOptions): Promise<T> {
  // Refresh the Kula token if it's within the pre-expiry window BEFORE we send —
  // every AgentForge request carries the token as its Bearer (and the backend
  // stores it), so refreshing here means the token AgentForge receives + reuses
  // is always fresh. No-op unless the token is actually near expiry.
  await ensureFreshKulaToken();

  const method = opts.method ?? 'GET';
  const config: AxiosRequestConfig = {
    params: opts.params,
    signal: opts.signal,
    onUploadProgress: opts.onUploadProgress
      ? (e) => {
          if (e.total && e.total > 0) {
            opts.onUploadProgress?.(Math.round((e.loaded / e.total) * 100));
          }
        }
      : undefined,
  };

  let raw: unknown;
  try {
    raw = await dispatch(method, opts.url, opts.body, config);
  } catch (err) {
    const responseData = (err as AxiosError)?.response?.data;
    const fromBody =
      responseData !== undefined ? extractError(responseData) : null;
    throw fromBody ?? err;
  }

  const maybeError = extractError(raw);
  if (maybeError) throw maybeError;

  return raw as T;
}
